// Short, authored Adventure chapters: physical learning changes the course.
import * as THREE from "three";
import { B } from "./blocks.js";
import { nameTag } from "./models.js";
import { cartModel, dragonMountModel } from "./expansion.js";
import { disposeObj } from "./entities.js";
import { audio } from "./audio.js";
import { ensureInventory, addItem } from "./crafting.js";
import { ensureExpansion } from "./expansion-rules.js";
export const EXPEDITIONS = {
  mine: {
    name: "Crystal Mine Express",
    icon: "🛤️",
    reward: "powered_rail",
    count: 12,
  },
  sky: { name: "Ember’s Sky Rescue", icon: "🐉", reward: "crystal", count: 8 },
  temple: { name: "The Secret Temple", icon: "🏛️", reward: "wire", count: 16 },
};
export function generateExpedition(w, id) {
  const kind = id.split("-")[1],
    spec = EXPEDITIONS[kind],
    ents = [],
    path = [];
  for (let z = -4; z <= 106; z++) {
    const width = z >= 70 && z <= 84 ? 8 : 4;
    for (let x = -width; x <= width; x++) {
      if (z >= 18 && z <= 21 && Math.abs(x) <= 1) continue;
      w.set(x, 40, z, kind === "temple" ? B.SANDSTONE : B.PLANKS);
      w.set(x, 39, z, B.STONE_BRICK);
    }
    if (z % 4 === 0) {
      path.push({ x: 0, y: 41, z });
      ents.push({ type: "coin", x: 0.5, y: 42, z: z + 0.5 });
    }
    if (z % 12 === 0)
      for (const x of [-4, 4]) {
        w.fill(x, 41, z, x, 43, z, B.LOG);
        w.set(x, 44, z, B.LAMP);
      }
  }
  // The missing bridge has no side route: 3 wide x 4 long after Sai solves it.
  w.fill(-4, 39, 18, 4, 40, 21, B.AIR);
  if (kind === "mine")
    for (let z = 29; z <= 59; z++) {
      for (const x of [-4, 4]) w.fill(x, 41, z, x, 46, z, B.STONE);
      w.fill(-4, 47, z, 4, 48, z, B.STONE);
      w.set(0, 41, z, B.RAIL);
      if (z % 4 === 0) w.set(3, 42, z, B.CRYSTAL);
    }
  if (kind === "sky") {
    w.fill(-4, 39, 30, 4, 40, 58, B.AIR);
    for (let z = 32; z < 59; z += 6) {
      w.fill(-9, 47, z, -6, 48, z + 2, B.CLOUD);
      w.fill(7, 52, z, 10, 53, z + 2, B.CLOUD);
    }
  }
  if (kind === "temple")
    for (let z = 32; z < 57; z += 3) {
      w.fill(-2, 39, z, 2, 40, z + 1, B.AIR);
      for (let x = -1; x <= 1; x++)
        ents.push({
          type: "crumble",
          x,
          y: 40,
          z,
          block: B.PLANKS,
          delay: 1.1,
        });
    }
  // Branch: a broad left treasure route and right coin route, rejoining safely.
  w.fill(-2, 41, 74, 2, 44, 80, B.BOOKSHELF);
  ents.push(
    { type: "star", x: 5.5, y: 42, z: 77.5 },
    { type: "chest", x: 6, y: 41, z: 79 },
    { type: "mob", kind: "slime", x: -5, y: 41, z: 78 },
  );
  ents.push(
    { type: "checkpoint", x: 0, y: 41, z: 2, start: true },
    { type: "checkpoint", x: 0, y: 41, z: 64 },
    { type: "portal", x: 0.5, y: 41, z: 102, active: false },
    { type: "sign", x: -3, y: 41, z: 7, text: "FOLLOW THE COINS" },
  );
  for (const z of [88, 95]) {
    w.fill(-4, 41, z, -4, 46, z, B.STONE_BRICK);
    w.fill(4, 41, z, 4, 46, z, B.STONE_BRICK);
    w.fill(-4, 46, z, 4, 46, z, B.GOLD_BLOCK);
  }
  return {
    spawn: { x: 0.5, y: 41, z: 0, yaw: 0 },
    killY: 25,
    endZ: 102,
    ents,
    path,
    islands: [],
    expedition: kind,
  };
}
const CLUES = [
  {
    z: 12,
    title: "Build the missing bridge",
    text: "The bridge is 3 blocks wide and 4 blocks long. How many blocks fill it?",
    choices: ["7", "12", "16"],
    answer: "12",
    hint: "Think of 4 rows of 3 blocks: 3 + 3 + 3 + 3.",
    explain: "3 × 4 = 12. Watch your bridge appear!",
  },
  {
    z: 67,
    title: "Read the explorer’s sign",
    text: "“The star is in the LEFT passage. The right passage has coins.” Where is the star?",
    choices: ["Left", "Right", "Behind"],
    answer: "Left",
    hint: "Read the first sentence again. It names the LEFT passage.",
    explain: "Follow the left passage for the star. Both routes lead onwards.",
  },
  {
    z: 89,
    title: "Open the temple portal",
    text: "The glowing stones count 8, 16, 24, __. What comes next?",
    choices: ["25", "30", "32"],
    answer: "32",
    hint: "Add 8 each time. 24 + 8 = ?",
    explain: "The pattern adds 8. 24 + 8 = 32. The portal is ready!",
  },
];
export class Expedition {
  constructor(L) {
    this.L = L;
    this.kind = L.spec.expedition;
    this.done = new Set();
    this.group = new THREE.Group();
    L.group.add(this.group);
    // The same physical bridge supports multiplication and inverse division.
    const clues = CLUES.map((q) => ({ ...q }));
    if (this.kind === "sky") {
      Object.assign(clues[0], {
        text: "The sky bridge needs 12 blocks. Each row uses 3 blocks. How many rows will you build?",
        choices: ["3", "4", "9"],
        answer: "4",
        hint: "Count in threes until you reach 12: 3, 6, 9, 12.",
        explain: "12 ÷ 3 = 4 rows. Your sky bridge is ready!",
      });
      Object.assign(clues[1], {
        text: "Ember’s map says: ‘Take the LEFT path to rescue the star. The right path has coins.’ Which path reaches the star?",
      });
      Object.assign(clues[2], {
        text: "The sky gates count 5, 10, 15, __. Which number lights the last gate?",
        choices: ["16", "20", "25"],
        answer: "20",
        hint: "Add 5 to the number before it. What is 15 + 5?",
        explain: "15 + 5 = 20. You found the sky route!",
      });
    } else if (this.kind === "temple") {
      Object.assign(clues[0], {
        text: "Share 12 bridge stones equally into 4 rows. How many stones belong in each row?",
        choices: ["3", "4", "8"],
        answer: "3",
        hint: "Try four equal groups. 3 + 3 + 3 + 3 makes 12.",
        explain: "12 ÷ 4 = 3 stones in each row. The bridge appears!",
      });
      Object.assign(clues[1], {
        text: "The ancient sign says: ‘Go LEFT for the star, then follow the path back to the centre.’ What should you do first?",
      });
      Object.assign(clues[2], {
        text: "The temple stones double each time: 2, 4, 8, __. What comes next?",
        choices: ["10", "12", "16"],
        answer: "16",
        hint: "Double means two of the same number. Add 8 + 8.",
        explain: "Double 8 is 16. The temple portal wakes up!",
      });
    }
    this.clues = clues.map((q, i) => {
      const tag = nameTag(
        i === 0
          ? "BUILD A BRIDGE"
          : i === 1
            ? "CHOOSE A PATH"
            : "PORTAL PATTERN",
        { color: "#ffe6a4", crown: false, scale: 2.4 },
      );
      tag.position.set(0.5, 44, q.z);
      this.group.add(tag);
      return { ...q, tag };
    });
    this.vehicle = this.kind === "sky" ? dragonMountModel() : cartModel();
    this.vehicle.position.set(0.5, 41, 28);
    this.group.add(this.vehicle);
    this.vehicle.visible = this.kind !== "temple";
    this.ride = null;
    this.question = null;
    this.rewarded = false;
  }
  ask(i) {
    if (this.done.has(i) || this.L.state !== "play") return;
    this.question = i;
    this.attempt = 0;
    this.L.state = "quiz";
    this.L.player.frozen = true;
    this.L.game.input.reset();
    this.renderQuestion();
  }
  renderQuestion(hint = "") {
    const q = this.clues[this.question],
      g = this.L.game;
    g.ui.modal(
      `<div class="panel expansion-panel"><h2>${q.title}</h2><p class="puzzle-story">${q.text}</p><div class="puzzle-choices">${q.choices.map((c) => `<button class="btn btn-blue" data-act="expeditionAnswer" data-answer="${c}">${c}</button>`).join("")}</div><p role="status">${hint}</p><button class="btn btn-dark" data-act="expeditionHint">💡 Hint</button><button class="btn btn-dark" data-act="expeditionRead">🔊 Read it</button><button class="btn btn-dark" data-act="expeditionLater">Back to the adventure</button></div>`,
      "modal-expansion",
    );
  }
  answer(answer) {
    const i = this.question,
      q = this.clues[i];
    if (!q || this.L.state !== "quiz" || this.done.has(i)) return false;
    if (answer !== q.answer) {
      this.attempt++;
      this.renderQuestion(q.hint);
      return false;
    }
    this.done.add(i);
    this.L.stats.asked++;
    if (!this.attempt) this.L.stats.firstTry++;
    this.L.stats.gates++;
    this.L.game.onAnswer(true, !this.attempt, true);
    this.L.stats.coins += 10;
    this.L.stats.xp += 15;
    if (i === 0) {
      for (let z = 18; z <= 21; z++)
        for (let x = -1; x <= 1; x++) {
          this.L.world.set(x, 40, z, B.PLANKS, true);
          this.L.fx.particles.sparkle(
            new THREE.Vector3(x, 41, z),
            "#ffe89d",
            4,
          );
        }
      this.L.world.rebuild();
    }
    if (this.done.size === 3) this.L.portal.open();
    this.L.game.ui.toast(q.explain, "green", 5000);
    audio.play("unlock");
    this.close();
    return true;
  }
  close() {
    this.question = null;
    this.L.game.ui.closeModal();
    this.L.state = "play";
    this.L.player.frozen = false;
    this.L.player.invuln = 2;
    this.L.game.input.reset();
  }
  board() {
    if (this.ride || this.L.state !== "play") return;
    this.ride = { t: 0 };
    if(this.L.pet)this.L.pet.obj.visible=false;
    this.L.player.vel.set(0, 0, 0);
    this.L.game.ui.toast(
      this.kind === "sky"
        ? "Hold left or right to swoop through the clouds!"
        : "Hold left or right to lean. Enjoy your crystal-mine ride!",
      "blue",
      5000,
    );
  }
  drive(dt, input) {
    if (!this.ride) return false;
    const L = this.L,
      r = this.ride;
    r.t = Math.min(1, r.t + dt / 7);
    const t = r.t;
    L.player.pos.set(
      0.5 + Math.sin(t * Math.PI) * input.moveX * 2,
      41 + (this.kind === "sky" ? Math.sin(t * Math.PI) * 13 : 0),
      28 + t * 34,
    );
    L.player.vel.set(0, 0, 0);
    L.player.ch.root.position
      .copy(L.player.pos)
      .add(new THREE.Vector3(0, this.kind === "sky" ? 0.5 : 0.2, 0));
    L.player.ch.root.rotation.y = 0;
    L.player.ch.update(dt, { grounded: true, riding: true });
    this.vehicle.position.copy(L.player.pos);
    this.vehicle.rotation.z = -input.moveX * 0.12;
    this.vehicle.userData.wings?.forEach(
      (w, i) => (w.rotation.z = Math.sin(L.time * 9) * 0.5 * (i ? 1 : -1)),
    );
    if (t === 1) {
      this.ride = null;
      if(L.pet)L.pet.obj.visible=true;
      L.player.spawn(new THREE.Vector3(0.5, 41, 63));
    }
    return true;
  }
  update() {
    const L = this.L;
    if (L.state !== "play" || this.ride) return;
    for (let i = 0; i < this.clues.length; i++) {
      const q = this.clues[i];
      q.tag.visible = !this.done.has(i);
      if (
        !this.done.has(i) &&
        L.player.pos.distanceTo(new THREE.Vector3(0.5, 41, q.z)) < 4
      )
        L.offerAction(
          this,
          q.title,
          "SOLVE",
          "🧠",
          new THREE.Vector3(0.5, 43, q.z),
          () => this.ask(i),
        );
    }
    if (
      this.kind !== "temple" &&
      L.player.pos.distanceTo(this.vehicle.position) < 4 &&
      L.player.pos.z < 32
    )
      L.offerAction(
        this,
        this.kind === "sky"
          ? "Ride Ember over the clouds"
          : "Board the minecart",
        "RIDE",
        this.kind === "sky" ? "🐉" : "🛤️",
        this.vehicle.position.clone().add(new THREE.Vector3(0, 2, 0)),
        () => this.board(),
      );
  }
  award() {
    const s = this.L.game.save.data;
    ensureInventory(s);
    if (!s.discovery.expansion) ensureExpansion(s.discovery);
    const a = s.discovery.expansion,
      r = EXPEDITIONS[this.kind];
    if (a.adventures[this.kind]) return;
    if (!addItem(s.discovery, r.reward, r.count)) {
      this.L.game.ui.toast(
        "Make space in your backpack and replay to claim the discovery bundle.",
        "blue",
      );
      return;
    }
    a.adventures[this.kind] = true;
    this.L.stats.gems += 3;
    this.L.game.ui.toast(
      `Discovery reward: ${r.count} ${r.reward.replaceAll("_", " ")} for Explore! Blueprint learned.`,
      "green",
      6000,
    );
  }
  dispose() {
    for (const q of this.clues) q.tag.material.map.dispose();
    this.group.removeFromParent();
    disposeObj(this.group);
  }
}
